/* Tabiji: el tren (los días), la ventanilla (un día) y las hojas (el detalle).
   Rutas: #/tren y #/dia/14. Durante el viaje se abre directamente el día de hoy. */
import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { arrancarSincronizacion } from './datos/apuntes.ts';
import { arrancarUbicacion } from './datos/ubicacion.ts';
import { enriquecer } from './datos/enriquecer.ts';
import { precargarFotos } from './datos/fotos.ts';
import { pedirTiempo } from './datos/meteo.ts';
import type { Lugar, Viaje } from './datos/tipos.ts';
import { useViaje } from './datos/viaje.ts';
import { Acceso, Esperando, Invitacion } from './componentes/Acceso.tsx';
import { Actualizar } from './componentes/Actualizar.tsx';
import { comprobarSesion, useSesion } from './datos/sesion.ts';
import { Tren } from './componentes/Tren.tsx';
import { Ventana } from './componentes/Ventana.tsx';
import type { BloqueV } from './escena/bloques.ts';
import { Motor, reducido } from './escena/motor.ts';
import { f1 } from './escena/util.ts';
import { HojaBilletes } from './hojas/Billetes.tsx';
import { HojaDia } from './hojas/Dia.tsx';
import { HojaExamenes } from './hojas/Examenes.tsx';
import { HojaGuia, type PestanaGuia } from './hojas/Guia.tsx';
import { HojaHotel } from './hojas/Hotel.tsx';
import { HojaCuanto } from './hojas/Cuanto.tsx';
import { HojaGasto } from './hojas/Gasto.tsx';
import { HojaParada } from './hojas/Parada.tsx';
// El mapa (MapLibre) solo se descarga y se carga al abrirlo
const HojaMapa = lazy(() => import('./hojas/Mapa.tsx').then(m => ({ default: m.HojaMapa })));
import { ahoraMin, hoyN, registrarDias, useSimulado, useTic } from './tiempo.ts';

type Ruta = { modo: 'tren' } | { modo: 'ventana'; n: number };
type HojaAbierta =
  | { tipo: 'parada'; b: BloqueV }
  | { tipo: 'dia' | 'billetes' | 'gasto' | 'mapa' | 'cuanto' }
  | { tipo: 'guia'; pestana: PestanaGuia }
  | { tipo: 'hotel'; lugar: Lugar }
  | { tipo: 'examenes'; dia: number }
  | null;

/** La puerta: sin sesión, la entrada (o el enlace de invitación); con ella, el viaje. */
export function App() {
  const s = useSesion();
  const [hash, setHash] = useState(location.hash);
  useEffect(() => { void comprobarSesion(); }, []);
  useEffect(() => { const f = () => setHash(location.hash); addEventListener('hashchange', f); return () => removeEventListener('hashchange', f); }, []);
  const invitacion = /^#\/invitacion\/([\w-]{20,80})$/.exec(hash)?.[1];
  if (invitacion && s.fase !== 'dentro') return <Invitacion token={invitacion} />;
  if (s.fase === 'fuera') return <Acceso />;
  if (s.fase === 'comprobando' && !s.yo) return <Esperando />;      // primera vez en este móvil: no se pinta nada a medias
  return <Viajero />;
}

function Viajero() {
  const { viaje, error } = useViaje();
  useEffect(() => arrancarSincronizacion(), []);
  useEffect(() => arrancarUbicacion(), []);

  // Que el navegador no borre por falta de espacio lo guardado (mapas, fotos, el viaje y lo apuntado sin subir)
  useEffect(() => { void navigator.storage?.persist?.().catch(() => {}); }, []);
  if (!viaje) {
    return (
      <div className="app" data-modo="tren">
        <div className="cielo" aria-hidden="true" />
        <p className="arranque" role="status">{error ? `No llega el viaje (${error}). Sin conexión la primera vez no hay copia que enseñar.` : 'Subiendo al tren…'}</p>
      </div>
    );
  }
  return <ConViaje viaje={viaje} />;
}

/** El viaje con lo que añade la app (los exámenes como bloques del día), calculado una vez por importación. */
function ConViaje({ viaje }: { viaje: Viaje }) {
  const v = useMemo(() => {
    const e = enriquecer(viaje);
    registrarDias(e.viaje.inicio, n => e.dias[n - 1]?.bloques.reduce((m, b) => Math.max(m, b.fin ?? b.ini ?? 0), 0));
    return e;
  }, [viaje]);
  useEffect(() => { void pedirTiempo(v); precargarFotos(v); }, [v]);
  return <Tabiji v={v} />;
}

function Tabiji({ v }: { v: Viaje }) {
  useSimulado();
  const total = v.dias.length;
  const hoy = hoyN(v.viaje.inicio);
  const acotar = (n: number) => Math.min(total, Math.max(1, n));

  const leerRuta = useCallback((): Ruta => {
    const m = location.hash.match(/#\/dia\/(\d+)/);
    if (m) return { modo: 'ventana', n: acotar(+m[1]) };
    if (location.hash === '#/tren' || hoy < 1 || hoy > total) return { modo: 'tren' };
    return { modo: 'ventana', n: hoy };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoy, total]);

  const app = useRef<HTMLDivElement>(null), cielo = useRef<HTMLDivElement>(null), svg = useRef<SVGSVGElement>(null), marco = useRef<HTMLDivElement>(null);
  const [motor, setMotor] = useState<Motor | null>(null);
  const [ruta, setRuta] = useState<Ruta>(leerRuta);
  const [ultimo, setUltimo] = useState(() => (ruta.modo === 'ventana' ? ruta.n : acotar(hoy)));
  const [hoja, setHoja] = useState<HojaAbierta>(null);
  const [tamano, setTamano] = useState(`${innerWidth}x${innerHeight}`);
  const pendiente = useRef<DOMRect | null>(null);

  useLayoutEffect(() => {
    const m = new Motor(svg.current!);
    m.alArrastrar = si => app.current?.toggleAttribute('data-arrastrando', si);
    setMotor(m);
    return () => m.destruir();
  }, []);

  // Rutas: atrás/adelante del navegador
  useEffect(() => {
    const f = () => { setHoja(null); setRuta(leerRuta()); };
    addEventListener('popstate', f);
    return () => removeEventListener('popstate', f);
  }, [leerRuta]);
  useEffect(() => { if (ruta.modo === 'ventana') setUltimo(ruta.n); }, [ruta]);

  // Al girar el móvil o cambiar la ventana, se vuelve a dibujar
  useEffect(() => {
    let t = 0;
    const f = () => { clearTimeout(t); t = window.setTimeout(() => setTamano(`${innerWidth}x${innerHeight}`), 200); };
    addEventListener('resize', f);
    return () => removeEventListener('resize', f);
  }, []);

  const navegar = (r: Ruta, empujar = true) => {
    setRuta(r);
    const h = r.modo === 'tren' ? '#/tren' : `#/dia/${r.n}`;
    if (location.hash !== h) history[empujar ? 'pushState' : 'replaceState'](null, '', `${location.search}${h}`);
  };

  // La ventanilla del vagón se abre hasta llenar la pantalla mientras el tren se desvanece
  useLayoutEffect(() => {
    const r = pendiente.current;
    if (!r || ruta.modo !== 'ventana') return;
    pendiente.current = null;
    if (reducido()) return;
    const desde = `inset(${f1(r.top)}px ${f1(innerWidth - r.right)}px ${f1(innerHeight - r.bottom)}px ${f1(r.left)}px round 12px)`;
    const opciones = { duration: 800, easing: 'cubic-bezier(.7,0,.2,1)' };
    for (const el of [cielo.current, svg.current]) el?.animate([{ clipPath: desde }, { clipPath: 'inset(0px 0px 0px 0px round 0px)' }], opciones);
    marco.current?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 500, delay: 450, fill: 'backwards' });
    document.getElementById('ventana')?.animate([{ opacity: 0, transform: 'translateY(24px)' }, { opacity: 1, transform: 'none' }], { duration: 450, delay: 550, easing: 'cubic-bezier(.2,.9,.2,1)', fill: 'backwards' });
  }, [ruta]);

  // `?abrir=parada|dia|billetes|gasto` abre una hoja al cargar (para probar y para accesos directos)
  useEffect(() => {
    if (!motor) return;
    const abrir = new URLSearchParams(location.search).get('abrir');
    if (abrir === 'dia' || abrir === 'billetes' || abrir === 'gasto' || abrir === 'mapa' || abrir === 'cuanto') setHoja({ tipo: abrir });
    if (abrir === 'guia') setHoja({ tipo: 'guia', pestana: (new URLSearchParams(location.search).get('pestana') as PestanaGuia) ?? 'antes' });
    if (abrir === 'parada' && ruta.modo === 'ventana') {
      const t = setTimeout(() => { const b = motor.bl.find(x => x.t.ini <= motor.p && motor.p < x.t.fin) ?? motor.bl.find(x => x.t.ini > motor.p); if (b) setHoja({ tipo: 'parada', b }); }, 50);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motor]);

  // La víspera de un día de exámenes, a partir de las 18:00, la hoja de exámenes se abre sola (una vez).
  // Antes no: el 17 hay exámenes por la tarde y no puede taparlos el aviso de los del 18.
  useTic(60_000);
  const tarde = ahoraMin() >= 18 * 60;
  useEffect(() => {
    const manana = hoy + 1, clave = `tabiji.visto.examenes.${manana}`;
    if (!tarde || !v.examenes.some(e => e.dia === manana) || localStorage.getItem(clave)) return;
    localStorage.setItem(clave, String(Date.now()));
    setHoja(h => h ?? { tipo: 'examenes', dia: manana });
  }, [hoy, v, tarde]);

  const n = ruta.modo === 'ventana' ? ruta.n : ultimo;
  const dia = v.dias[n - 1];
  const cerrarHoja = useCallback(() => setHoja(null), []);
  // Desde el buscador: al día de esa parada, el paisaje hasta su hora y su hoja abierta
  const irABloque = (k: number, id: string) => {
    setHoja(null);
    navegar({ modo: 'ventana', n: acotar(k) });
    setTimeout(() => { const b = motor?.bl.find(x => x.id === id); if (b && motor) { motor.irA(b.mid, 900); setHoja({ tipo: 'parada', b }); } }, 400);
  };

  return (
    <div className="app" ref={app} data-modo={ruta.modo} data-hoja={hoja ? '' : undefined} style={{ '--linea': `var(--c-${dia.ciudad})` } as CSSProperties}>
      <div className="cielo" ref={cielo} aria-hidden="true" />
      <svg className="escena" ref={svg} aria-hidden="true" />
      <div className="lluvia" aria-hidden="true" />
      <div className="marco" ref={marco} aria-hidden="true" />
      {motor && (
        <>
          <Tren motor={motor} v={v} activa={ruta.modo === 'tren'} hoy={hoy} desde={ultimo} tamano={tamano}
            onEntrar={(k, rect) => { pendiente.current = rect; navegar({ modo: 'ventana', n: k }); }}
            onGuia={pestana => setHoja({ tipo: 'guia', pestana })} onCuanto={() => setHoja({ tipo: 'cuanto' })} />
          <Ventana motor={motor} v={v} dia={dia} total={total} activa={ruta.modo === 'ventana'} esHoy={dia.n === hoy} tamano={tamano}
            onTren={() => navegar({ modo: 'tren' })} onDia={k => navegar({ modo: 'ventana', n: acotar(k) })}
            onParada={b => setHoja({ tipo: 'parada', b })} onHoja={tipo => setHoja({ tipo })} bloqueado={hoja !== null}
            onGuia={() => setHoja({ tipo: 'guia', pestana: hoy < 1 ? 'antes' : 'compras' })}
            onFrases={() => setHoja({ tipo: 'guia', pestana: 'frases' })} onCuanto={() => setHoja({ tipo: 'cuanto' })} />
        </>
      )}
      {hoja?.tipo === 'parada' && <HojaParada v={v} dia={dia} b={hoja.b} esHoy={dia.n === hoy} onCerrada={cerrarHoja} onFrases={() => setHoja({ tipo: 'guia', pestana: 'frases' })} />}
      {hoja?.tipo === 'dia' && motor && (
        <HojaDia v={v} dia={dia} bl={motor.bl} esHoy={dia.n === hoy} onIr={b => motor.irA(b.mid, 900)} onCerrada={cerrarHoja}
          onHotel={lugar => setHoja({ tipo: 'hotel', lugar })} onGuia={pestana => setHoja({ tipo: 'guia', pestana })} />
      )}
      {hoja?.tipo === 'guia' && <HojaGuia v={v} inicial={hoja.pestana} onCerrada={cerrarHoja} onIr={irABloque} />}
      {hoja?.tipo === 'hotel' && <HojaHotel v={v} lugar={hoja.lugar} onCerrada={cerrarHoja} />}
      {hoja?.tipo === 'examenes' && <HojaExamenes v={v} dia={hoja.dia} onCerrada={cerrarHoja} />}
      {hoja?.tipo === 'billetes' && <HojaBilletes v={v} n={ruta.modo === 'ventana' ? ruta.n : acotar(hoy)} hoy={hoy} onCerrada={cerrarHoja} />}
      {hoja?.tipo === 'mapa' && <Suspense fallback={null}><HojaMapa v={v} dia={dia} esHoy={dia.n === hoy} onCerrada={cerrarHoja} onParada={b => setHoja({ tipo: 'parada', b: motor?.bl.find(x => x.id === b.id) ?? b })} /></Suspense>}
      {hoja?.tipo === 'gasto' && <HojaGasto v={v} n={ruta.modo === 'ventana' ? ruta.n : hoy} onCerrada={cerrarHoja} />}
      {hoja?.tipo === 'cuanto' && <HojaCuanto v={v} onCerrada={cerrarHoja} />}
      <Actualizar />
    </div>
  );
}
